<script setup>
import {onMounted, ref, watchEffect} from "vue";
import RequiredInformations from "@/components/shared/RequiredInformations.vue";
import {getLoggedStatus, getResource, baseUrl, citizenEvent, getRefererParams} from "@/api/httpApi";
import { useStore } from "vuex";
import * as Yup from "yup";
import {showFormToast, validateForm} from "@/utility";

const store = useStore();
const emit = defineEmits(["previous", "next"]);
const informations = ref(["Richiedente"]);
const resource = ref();
const firstName = ref(store.state.user?.firstName || "");
const lastName = ref(store.state.user?.lastName || "");
const fiscalCode = ref(store.state.user?.fiscalCode || "");
const phone = ref(store.state.user?.phone || "");
const mobilePhone = ref(store.state.user?.mobilePhone || "");
const email = ref(store.state.user?.email || "");
const isRecapping = ref(store.state.isRecapping || undefined);

const formErrors = ref({
  firstName: { error: false, message: "" },
  lastName: { error: false, message: "" },
  email: { error: false, message: "" },
  fiscalCode: { error: false, message: "" },
  phone: { error: false, message: "" },
  mobilePhone: { error: false, message: "" },
  userAuthenticated: { error: false, message: "" },
});
const showSpidLink = ref(false);
const userData = ref(undefined);
const step4Form = ref();
const userAuthenticated = ref(false);
const disableInput = ref(false);

const isFirstNameRequired = () => resource?.value?.requiredFields?.includes("FIRST_NAME");
const isLastNameRequired = () => resource?.value?.requiredFields?.includes("LAST_NAME");
const isEmailRequired = () => resource?.value?.requiredFields?.includes("EMAIL");
const isFiscalCodeRequired = () => resource?.value?.requiredFields?.includes("FISCAL_CODE");
const isPhoneRequired = () => resource?.value?.requiredFields?.includes("PHONE");
const isMobilePhoneRequired = () => resource?.value?.requiredFields?.includes("MOBILE_PHONE");
const isLoginRequired = () => resource?.value?.loginRequired || false;

onMounted(async () => {
});

function createStep4Form() {

  step4Form.value = Yup.object().shape({
    userAuthenticated: Yup.string().when([], {
      is: () => isLoginRequired(),
      then: (schema) =>  schema.oneOf(['true'], "Richiesto accesso SPID o CIE"),
      otherwise: (schema) => schema.nullable()
    }),
    firstName: Yup.string().when([], {
      is: () => isFirstNameRequired(),
      then: (schema) =>  schema.required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    lastName: Yup.string().when([], {
      is: () => isLastNameRequired(),
      then: (schema) =>  schema.required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    email: Yup.string().when([], {
      is: () => isEmailRequired(),
      then: (schema) =>  schema.email("Inserire indirizzo email valido").required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    fiscalCode: Yup.string().when([], {
      is: () => isFiscalCodeRequired(),
      then: (schema) => schema.trim().matches(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i , "Campo non valido").required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    phone: Yup.string().when([], {
      is: () => isPhoneRequired(),
      then: (schema) => schema.min(5, "Numero di caratteri non valido").max(12, "Numero di caratteri non valido").required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
    mobilePhone: Yup.string().when([], {
      is: () => isMobilePhoneRequired(),
      then: (schema) => schema.matches(/^\d{5,12}$/ , "Campo non valido").required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    }),
  });
}

watchEffect(async () => {
  if (
    store.state.resourceId &&
    (!resource.value || resource.value.resourceId !== store.state.resourceId)
  ) {
    try {
      resource.value = await getResource(store.state.resourceId);
      await getUserInfo();
      createStep4Form();
    } catch (err) {
      console.error(err);
      resource.value = undefined;
    }
  }
});


async function getUserInfo() {
  let data = await getLoggedStatus();
  data = (data.id > 0) ? data : await getRefererParams();

  userData.value = data;
  if (!data) {
    firstName.value = "";
    lastName.value = "";
    fiscalCode.value = "";
    email.value = "";
  }

  showSpidLink.value = !data?.roles?.includes("cittadino") && !(!resource.value?.loginRequired && data?.roles?.includes("persona"));

  //La proprietà "roles" è stata valorizzata con "persona" per distinguere il caso di dati provenienti da un referer
  if (data?.roles?.includes("cittadino") || data?.roles?.includes("persona")) {
    disableInput.value = true;
    firstName.value = data.firstName || "";
    lastName.value = data.lastName || "";
    fiscalCode.value = data.fiscalCode || "";
    email.value = data.email || "";
    mobilePhone.value = data.mobilePhone || "";
    userAuthenticated.value = data?.roles?.includes("cittadino") || false;
  }
}

watchEffect(() => {
  if (resource.value) {
    store.commit("patchUser", {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      ...(isFiscalCodeRequired()
        ? { fiscalCode: fiscalCode.value }
        : {}),
      ...(isPhoneRequired() ? { phone: phone.value } : {}),
      ...(isMobilePhoneRequired()
        ? { mobilePhone: mobilePhone.value }
        : {}),
    });
  }
});

async function nextStep(autoSubmit, goTo = false) {
  setTimeout(() => {
    validateForm(step4Form.value, 'step4Form', {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      fiscalCode: fiscalCode.value,
      phone: phone.value,
      mobilePhone: mobilePhone.value,
      userAuthenticated: userAuthenticated.value
    }, () => {
      if (autoSubmit && goTo) {
        // se tutto è valido procedo allo step successivo
        citizenEvent(
          {
            message: "Utente compila lo step richiedente",
            stepId: 4,
            stepType: "intermedio",
            resourceId: store?.state?.resourceId ?? ''
          }
        );

        emit('goto', 4);
      } else if (autoSubmit) {
        citizenEvent(
          {
            message: "Utente compila lo step richiedente",
            stepId: 4,
            stepType: "intermedio",
            resourceId: store?.state?.resourceId ?? ''
          }
        );

        emit('next');
      }
    }, (errors) => {
      // in presenza di errori di validazione mostro il toast di errore
      showFormToast('step4Form', errors);
    }, () => {
      // dopo la prima validazione resto in ascolto di ogni cambiamento di input per rieseguire una validazione in tempo reale
      nextStep(false);
    });
  }, 300);
}
</script>

<template>
<form id="step4Form" @submit.prevent>
  <div class="container-fluid row mx-auto p-2">
    <div class="col-3 border-end pe-0 d-none d-md-block">
      <RequiredInformations :informations="informations" :progress="80" />
    </div>
    <div class="col-12 px-0 ps-md-5 pe-md-2 mb-5 pt-4 col-md-9">
      <div
        class="bg-light p-4 shadow-sm"
        :id="encodeURIComponent('Richiedente')">
        <h2 class="h3 mb-3">Richiedente*</h2>
        <p class="card-text" v-if="showSpidLink">
          Hai un'identità digitale SPID o CIE?
          <a :href="`${baseUrl}/wso2silfi/connect/cittadino?destination=/${store.state.appRoute}`">Accedi</a>
          <input
            id="userAuthenticated"
            name="userAuthenticated"
            type="hidden"
            v-model="userAuthenticated"
            :required="isLoginRequired()"
            data-hidden="true"
            data-yup-label="Autenticazione"
          />
        </p>
        <div class="bg-white p-4">
          <div class="form-group mt-4">
            <label class="active" for="firstName">{{`Inserisci il tuo nome ${isFirstNameRequired() ? "*" : ""}`}}</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              class="form-control bg-white"
              placeholder="Nome"
              aria-describedby="formGroupNameInputHelper"
              :aria-label="`Inserisci il tuo nome ${
              formErrors.firstName.message ? `- ${formErrors.firstName.message}` : ''
            }`"
              v-model="firstName"
              :disabled="disableInput"
              :required="isFirstNameRequired()"
              data-yup-label="Nome"
            />
          </div>

          <div class="form-group mt-4">
            <label class="active" for="lastName">{{`Inserisci il tuo cognome ${isLastNameRequired() ? "*" : ""}`}}</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              class="form-control bg-white"
              placeholder="Cognome"
              aria-describedby="formGroupSurnameInputHelper"
              :aria-label="`Inserisci il tuo cognome ${
              formErrors.lastName.message
                ? `- ${formErrors.lastName.message}`
                : ''
            }`"
              v-model="lastName"
              :disabled="disableInput"
              :required="isLastNameRequired()"
              data-yup-label="Cognome"
            />
          </div>

          <div class="form-group mt-4">
            <label class="active" for="email">{{`Inserisci la tua email ${isEmailRequired() ? "*" : ""}`}}</label>
            <input
              id="email"
              name="email"
              type="email"
              class="form-control"
              placeholder="Email"
              aria-describedby="formGroupEmailInputHelper"
              :aria-label="`Inserisci la tua email ${
              formErrors.email.message ? `- ${formErrors.email.message}` : ''
            }`"
              v-model="email"
              :required="isEmailRequired()"
              data-yup-label="Email"
            />
          </div>

          <div v-if="isFiscalCodeRequired()" class="form-group mt-4">
            <label class="active" for="fiscalCode">{{`Inserisci il tuo codice fiscale ${isFiscalCodeRequired() ? "*" : ""}`}}</label>
            <input
              id="fiscalCode"
              name="fiscalCode"
              type="text"
              class="form-control bg-white"
              placeholder="Codice fiscale"
              aria-describedby="formGroupSurnameInputHelper"
              :aria-label="`Inserisci il tuo codice fiscale ${
              formErrors.fiscalCode.message
                ? `- ${formErrors.fiscalCode.message}`
                : ''
            }`"
              v-model="fiscalCode"
              :disabled="disableInput"
              :required="isFiscalCodeRequired()"
              data-yup-label="Codice fiscale"
            />
          </div>

          <div v-if="isPhoneRequired()" class="form-group mt-4">
            <label class="active" for="phone">{{`Inserisci il tuo numero di telefono ${isPhoneRequired() ? "*" : ""}`}}</label>
            <input
              id="phone"
              name="phone"
              type="text"
              class="form-control bg-white"
              placeholder="Telefono fisso"
              aria-describedby="formGroupSurnameInputHelper"
              :aria-label="`Inserisci il tuo numero di telefono ${
              formErrors.phone.message ? `- ${formErrors.phone.message}` : ''
            }`"
              v-model="phone"
              :required="isPhoneRequired()"
              data-yup-label="Telefono"
            />
          </div>

          <div v-if="isMobilePhoneRequired()" class="form-group mt-4">
            <label class="active" for="mobilePhone">{{`Inserisci il tuo numero di cellulare ${isMobilePhoneRequired() ? "*" : ""}`}}</label>
            <input
              id="mobilePhone"
              name="mobilePhone"
              type="text"
              class="form-control bg-white"
              placeholder="Cellulare"
              aria-describedby="formGroupSurnameInputHelper"
              :aria-label="`Inserisci il tuo numero di cellulare ${
              formErrors.mobilePhone.message
                ? `- ${formErrors.mobilePhone.message}`
                : ''
            }`"
              v-model="mobilePhone"
              :required="isMobilePhoneRequired()"
              data-yup-label="Telefono mobile"
            />
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-between mt-4">
        <button
          type="button"
          class="btn btn-outline-primary"
          @click="
            (e) => {
              emit('previous');
            }">
          Indietro
        </button>
        <button
          v-if="isRecapping"
          type="button"
          class="btn btn-outline-primary"
          @click="nextStep(true, true)">
          Salva
        </button>
        <button
          type="submit"
          class="btn btn-outline-primary"
          @click="nextStep(true)">
          Avanti
        </button>
      </div>
    </div>
  </div>
</form>
</template>
